import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAllGraphNodes, isGraphUnavailable } from 'DataGraph/dataGraphApi';
import { KindToggles, withoutHiddenKinds } from 'DataGraph/objectKinds';
import ObjectList, { FILTERS, selectObjects } from './ObjectList';
import { useObjectChoice, ObjectChoiceBar, resetUnsavedChoices } from './objectChoice';
import './SalesforceNetwork.css';

/**
 * Every object a Salesforce org's Describe found (from the Data Graph, which the Profile run
 * builds — one Describe per data object, no records read), with record counts and the per-record
 * storage estimate. Full width; a row opens that object's own page (`onOpenObject`), where the
 * map and the fields live (SalesforceObjectDetail).
 *
 * Mounted twice with the same code: on the Fingerprint page, where the owner chooses the objects
 * discovery samples (`selectable` on, the source's choice), and on a Data Graph, where the ticks are
 * THAT GRAPH's object list (`selectable` on with the page's `choice`, see useGraphDraft.graphChoice;
 * 2026-11-04). `planTags` (Data Graph only, { lowercased object: { text, tone, title } }) tags the
 * objects the graph's list saves or needs.
 */
// Per-source list state that outlives the list: opening an object unmounts the list, and coming
// back should find the same search, filter, kinds and sort. (An unsaved choice is kept by objectChoice.)
const listState = new Map();
/** Tests only: forget every remembered list and unsaved choice. */
export const resetObjectListState = () => { listState.clear(); resetUnsavedChoices(); };

/**
 * `source` is the platform row (toUiDataSource shape); its `selectedObjects` seeds the choice
 * (see useObjectChoice). `onSelectionSaved(list)` lets the page refresh its copy after a save.
 * `graphId` (Data Graph only) reads the lookup counts through that data graph, its manual links
 * included; the profile page passes none and sees the declared structure.
 */
// `extraColumn` (Data Graph only): ObjectList's extra column — how a ticked object is reached.
const SalesforceObjectsView = ({
  dataSourceId, graphId, nouns, jobActive, describing, onDescribe, onOpenObject, selectable = false, source, onSelectionSaved,
  choice: givenChoice = null, rowFilters = null, planTags = null, extraColumn = null,
}) => {
  const remembered = listState.get(String(dataSourceId)) || {};
  // The source's Discover choice unless the page hands one in (the Data Graph's own object list).
  const ownChoice = useObjectChoice({ dataSourceId, source, enabled: selectable && !givenChoice, onSaved: onSelectionSaved });
  const choice = givenChoice || ownChoice;
  const { picked } = choice;
  const [nodes, setNodes] = useState(null);
  const [graphState, setGraphState] = useState('loading');
  const [graphError, setGraphError] = useState(null);
  const [query, setQuery] = useState(remembered.query || '');
  // A data graph's page opens on its own objects (Selected) while it has any; the Fingerprint page on All.
  const [filter, setFilter] = useState(remembered.filter && (selectable || remembered.filter !== 'selected') ? remembered.filter
    : (givenChoice && givenChoice.kind === 'graph' && givenChoice.picked.size > 0 ? 'selected' : 'all'));
  const [sort, setSort] = useState(remembered.sort || { key: 'rows', dir: 'desc' });
  // Every object kind is listed until one is turned off (a Salesforce profile holds every queryable object).
  const [hiddenKinds, setHiddenKinds] = useState(() => new Set(remembered.hiddenKinds || []));
  const mounted = useRef(true);
  const wasActive = useRef(Boolean(jobActive));

  useEffect(() => () => { mounted.current = false; }, []);

  const loadNodes = useCallback(async () => {
    setGraphState('loading');
    try {
      const res = await getAllGraphNodes(dataSourceId, { graphId });
      if (!mounted.current) return;
      setNodes(res.items || []);
      setGraphState('ready');
      setGraphError(null);
    } catch (e) {
      if (!mounted.current) return;
      setNodes(null);
      if (isGraphUnavailable(e)) setGraphState('unavailable');
      else { setGraphState('error'); setGraphError((e && e.message) || 'Could not load the object graph.'); }
    }
  }, [dataSourceId, graphId]);

  useEffect(() => { loadNodes(); }, [loadNodes]);

  // A Describe that just finished (the page's poller flips jobActive true → false) fills the
  // list without a reload.
  useEffect(() => {
    if (wasActive.current && !jobActive) loadNodes();
    wasActive.current = Boolean(jobActive);
  }, [jobActive, loadNodes]);

  useEffect(() => {
    listState.set(String(dataSourceId), { query, filter, sort, hiddenKinds: [...hiddenKinds] });
  }, [dataSourceId, query, filter, sort, hiddenKinds]);

  const visible = useMemo(
    () => selectObjects(withoutHiddenKinds(nodes || [], hiddenKinds), { query, filter, sort, picked: selectable ? picked : null }),
    [nodes, hiddenKinds, query, filter, sort, picked, selectable]);
  const filters = selectable ? FILTERS : FILTERS.filter(([v]) => v !== 'selected');

  const itemNoun = (nouns && nouns.item) || 'objects';

  if (graphState === 'loading' && nodes == null) {
    return <div className='sfn-empty' data-testid='sf-objects-loading'>Loading {itemNoun}…</div>;
  }
  if (graphState === 'unavailable') {
    return (
      <div className='metadata-empty' style={{ padding: 32 }} data-testid='sf-objects-unavailable'>
        {jobActive ? (
          <>Describe in progress… this view refreshes when it completes.</>
        ) : (
          <>
            No object graph for this org yet. Run Describe first.
            {onDescribe && (
              <div style={{ marginTop: 12 }}>
                <button type='button' className='metadata-runbtn' data-testid='sf-objects-describe' disabled={describing} onClick={onDescribe}>
                  {describing ? 'Starting…' : 'Describe'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  }
  if (graphState === 'error') {
    return <div className='metadata-empty' style={{ padding: 32 }} data-testid='sf-objects-error'>{graphError}</div>;
  }

  return (
    <>
      <div className='sfn-head'>
        <h3>{itemNoun.charAt(0).toUpperCase() + itemNoun.slice(1)} ({(nodes || []).length})</h3>
        <input type='text' className='sfn-search' data-testid='sf-object-search' placeholder={`Search ${itemNoun}…`}
          value={query} onChange={(e) => setQuery(e.target.value)} />
        <div className='sfn-chips'>
          {filters.map(([v, label]) => (
            <button type='button' key={v} className={`sfn-chip${filter === v ? ' sfn-chip--on' : ''}`}
              data-testid={`sf-object-filter-${v}`} onClick={() => setFilter(v)}>{label}</button>
          ))}
        </div>
        <KindToggles items={nodes || []} hidden={hiddenKinds} onChange={setHiddenKinds} chipClass='sfn-chip' testId='sf-object-kinds' />
        {(query.trim() !== '' || filter !== 'all' || hiddenKinds.size > 0) && (
          <span className='sfn-count'>{visible.length} of {(nodes || []).length} {itemNoun} shown</span>
        )}
      </div>
      {selectable && <ObjectChoiceBar choice={choice} total={(nodes || []).length} itemNoun={itemNoun} />}
      <ObjectList items={visible} sort={sort} onSort={setSort} rowFilters={rowFilters} planTags={planTags} extraColumn={extraColumn}
        onSelect={(n) => onOpenObject && onOpenObject(n.table)} nouns={nouns}
        picked={selectable ? picked : null} onPick={choice.toggle} onPickAll={choice.pickAll}
        pickTitle={choice.kind === 'graph' ? 'The objects this data graph saves' : 'Choose the objects discovery samples'} />
    </>
  );
};

export default SalesforceObjectsView;
